import type { MouseEvent } from 'react';
import React, { useMemo, useState } from 'react';
import classnames from 'classnames';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LoginOrProfile } from 'components/LoginOrProfile/LoginOrProfile';
import { headerExclusionUrls, LanguageType } from 'common/constants';
import { AppRoutes } from 'common/types';
import { Select } from 'antd';
import { useAppDispatch, useAppSelector } from 'store';
import { usersSelectors } from 'store/slices/users/config';
import {
  generalSliceActions,
  generalSliceSelectors,
} from 'store/slices/general/config';
import { ContactUsForm } from 'components/ContactUsForm';
import { MenuWithBurger } from 'common/common_components/MenuWithBurger';
import { useIsScrolled } from 'hooks/useIsScrolled';
import { navItems } from './routes';
import styles from './NavigationPanel.module.scss';

export const NavigationPanel = () => {
  const navigate = useNavigate();
  const isScrolled = useIsScrolled();
  const location = useLocation();
  const [isNavPanelHovered, setIsNavPanelHovered] = useState(false);
  const profile = useAppSelector(usersSelectors.selectProfile);
  const profileIsPending = useAppSelector(
    usersSelectors.selectUsersPendingStatus,
  );
  const temporaryLanguage = useAppSelector(
    generalSliceSelectors.selectTemporaryLanguage,
  );
  const dispatch = useAppDispatch();
  const languagesOptions = useMemo(() => {
    return [
      {
        label: 'En',
        value: LanguageType.EN,
      },
      {
        label: 'Fr',
        value: LanguageType.FR,
      },
    ];
  }, []);

  const handleHover = () => {
    setIsNavPanelHovered(true);
  };
  const handleHoverLeave = () => {
    setIsNavPanelHovered(false);
  };

  const currentRoute = location.pathname;
  const handleSelectTempLanguage = (value: LanguageType) => {
    dispatch(generalSliceActions.setTemporaryLanguage(value));
  };
  const handleNavigate = (ev: MouseEvent<HTMLAnchorElement>) => {
    ev.preventDefault();
    navigate(AppRoutes.HOME);
    window.scrollTo({ top: 0 });
  };

  return (
    <div
      onMouseEnter={handleHover}
      onMouseLeave={handleHoverLeave}
      className={classnames(
        styles.navPanel,
        'flex items-end justify-between md:items-center',
        {
          'bg-transparent': window.scrollY === 0,
          'bg-white': isScrolled,
          [styles.scrolled]:
            isScrolled || currentRoute === AppRoutes.RESERVATION,
        },
      )}
    >
      <MenuWithBurger isHovered={isNavPanelHovered} menuItems={navItems} />
      <Link
        to="#"
        onClick={handleNavigate}
        className={classnames(styles.logoWrapper, 'absolute')}
      >
        <img
          className={styles.logo}
          src={`${process.env.PUBLIC_URL}/icons/nav-logo.png`}
          alt="logo"
          width={40}
          fetchPriority="low"
          decoding="async"
          height={40}
        />
      </Link>
      <div className={classnames(styles.btnContainer)}>
        <Select
          className={classnames('ant-select-wrapper md:hidden', {
            active: isScrolled || isNavPanelHovered,
          })}
          loading={profileIsPending}
          options={languagesOptions}
          onSelect={handleSelectTempLanguage}
          variant="borderless"
          value={temporaryLanguage}
          defaultValue={profile?.language || temporaryLanguage}
        />
        <ContactUsForm btnClassName={styles.btn} />
        {!headerExclusionUrls.some((item) =>
          location.pathname.includes(item),
        ) && <LoginOrProfile />}
      </div>
    </div>
  );
};
